import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import OrderKittingService from 'services/OrderKittingService';
import PreviewDialog from './PreviewDialog';
import {
  CatalogVariant,
  CURRENCIES,
  KittingRule,
  PRIORITIES,
  PRIORITY_LABELS,
  STORES,
  variantAdminUrl,
} from '../types';

interface ComponentRow {
  sku: string;
  quantity: string;
}

// One dropdown option per distinct SKU (a SKU can live on several products —
// e.g. the US 1-pack appears on five listings; the rule matches by SKU, so
// they're one option, with every carrying product named for verification).
interface SkuOption {
  sku: string;
  variants: CatalogVariant[];
  missing?: boolean; // saved on the rule but not in today's catalog
}

function toKebab(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function productSummary(variants: CatalogVariant[]): string {
  const titles = [...new Set(variants.map(v => `${v.productTitle} / ${v.variantTitle}`))];
  const shown = titles.slice(0, 2).join(' · ');
  return titles.length > 2 ? `${shown} · +${titles.length - 2} more` : shown;
}

function inventorySummary(variants: CatalogVariant[]): string {
  // Same-SKU variants share an inventory pool, so max (not sum) reflects stock.
  return `${Math.max(...variants.map(v => v.inventoryQuantity)).toLocaleString()} in stock`;
}

// Add/edit one substitution rule. SKUs and the kit variant are picked from the
// store's live catalog (names + inventory + admin links shown for
// verification) — free-typed SKUs silently never match, so there is no
// free-text path. The ruleId is derived from the kit SKU on create and
// immutable afterwards (it keys the stored rules object and the log).
export default function RuleDialog({
  open,
  rule,
  fixedStore,
  existingRuleIds,
  saving,
  onClose,
  onSave,
}: {
  open: boolean;
  rule: KittingRule | null; // null = create
  // Set when the dialog is opened from a store tab: new rules belong to that
  // store and the selector is locked.
  fixedStore?: string;
  existingRuleIds: string[];
  saving: boolean;
  onClose: () => void;
  onSave: (rule: KittingRule) => void;
}) {
  const service = React.useMemo(() => new OrderKittingService(), []);

  const [store, setStore] = React.useState<string>('US');
  const [priority, setPriority] = React.useState<number>(3);
  const [description, setDescription] = React.useState('');
  const [components, setComponents] = React.useState<ComponentRow[]>([{ sku: '', quantity: '2' }]);
  const [kitVariantId, setKitVariantId] = React.useState('');
  const [kitSku, setKitSku] = React.useState('');

  const [catalog, setCatalog] = React.useState<CatalogVariant[] | null>(null);
  const [shipbobLocation, setShipbobLocation] = React.useState<{ name: string } | null>(null);
  const [catalogError, setCatalogError] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setStore(rule?.store ?? fixedStore ?? 'US');
    setPriority(rule?.priority ?? 3);
    setDescription(rule?.description ?? '');
    setComponents(
      rule
        ? rule.components.map(c => ({ sku: c.sku, quantity: String(c.quantity) }))
        : [{ sku: '', quantity: '2' }],
    );
    setKitVariantId(rule ? String(rule.kitVariantId) : '');
    setKitSku(rule?.kitSku ?? '');
  }, [open, rule, fixedStore]);

  const loadCatalog = React.useCallback(async () => {
    setCatalog(null);
    setCatalogError(false);
    const result = await service.getCatalog(store);
    if (result) {
      setCatalog(result.variants);
      setShipbobLocation(result.shipbobLocation);
    } else setCatalogError(true);
  }, [service, store]);

  React.useEffect(() => {
    if (!open) return;
    loadCatalog();
  }, [open, loadCatalog]);

  const skuOptions = React.useMemo<SkuOption[]>(() => {
    if (!catalog) return [];
    const bySku = new Map<string, CatalogVariant[]>();
    for (const variant of catalog) {
      const list = bySku.get(variant.sku) ?? [];
      list.push(variant);
      bySku.set(variant.sku, list);
    }
    return [...bySku.entries()]
      .map(([sku, variants]) => ({ sku, variants }))
      .sort((a, b) => a.sku.localeCompare(b.sku));
  }, [catalog]);

  const skuOptionFor = (sku: string): SkuOption | null => {
    if (!sku) return null;
    return skuOptions.find(option => option.sku === sku) ?? { sku, variants: [], missing: true };
  };

  const parsedComponents = components.map(row => ({
    sku: row.sku.trim(),
    quantity: Number(row.quantity),
  }));
  const componentsBad = parsedComponents.some(
    c => !c.sku || !Number.isInteger(c.quantity) || c.quantity < 1,
  );
  const kitVariantIdNum = Number(kitVariantId.trim());
  const kitVariantBad = !Number.isInteger(kitVariantIdNum) || kitVariantIdNum < 1;
  const kitVariant = catalog?.find(v => v.variantId === kitVariantIdNum) ?? null;

  // Only variants ShipBob actually stocks can be offered as a kit: swapping a
  // line for something ShipBob has never seen edits cleanly in Shopify and then
  // strands the shipment. Variants of unknown status (store with no ShipBob
  // location) stay selectable — we cannot prove them bad.
  const kitOptions = React.useMemo(
    () => (catalog ?? []).filter(variant => variant.shipbobStocked !== false),
    [catalog],
  );
  const hiddenKitCount = (catalog?.length ?? 0) - kitOptions.length;
  const savedKitUnfulfillable = kitVariant != null && kitVariant.shipbobStocked === false;
  const ruleId = rule ? rule.ruleId : toKebab(kitSku) || (kitVariantBad ? '' : `rule-${kitVariantIdNum}`);
  const ruleIdTaken = !rule && existingRuleIds.includes(ruleId);
  const canSave = !saving && !componentsBad && !kitVariantBad && ruleId !== '' && !ruleIdTaken;

  // The rule exactly as it would be saved. Used for BOTH save and preview, so
  // previewing an edited rule shows the pending edits rather than the version
  // still on disk.
  const draftRule: KittingRule = {
    ruleId: ruleId || 'preview',
    store,
    enabled: rule ? rule.enabled : false,
    priority,
    description: description.trim(),
    components: parsedComponents,
    kitVariantId: kitVariantIdNum,
    kitSku: kitSku.trim(),
  };

  // Preview only needs valid components — matching never looks at the kit
  // variant — so the component picks can be checked before choosing a kit.
  const canPreview = !componentsBad;

  // Does the draft differ from what's saved? Only drives the preview's
  // "unsaved edits" note.
  const isDirty =
    rule != null &&
    (rule.priority !== priority ||
      rule.description !== description.trim() ||
      rule.kitVariantId !== kitVariantIdNum ||
      rule.kitSku !== kitSku.trim() ||
      JSON.stringify(rule.components) !== JSON.stringify(parsedComponents));

  const save = () => onSave(draftRule);

  const adminLink = (variant: CatalogVariant, title: string) => (
    <Tooltip title={title}>
      <IconButton
        size="small"
        component={Link}
        href={variantAdminUrl(store, variant)}
        target="_blank"
        rel="noreferrer"
      >
        <OpenInNewIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rule ? `Edit rule ${rule.ruleId}` : 'Add rule'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When an order contains every component below (in the given quantities),
          those lines are replaced with the kit variant. Rules apply to one
          storefront: SKUs, the kit variant and its pricing all belong to that
          store&apos;s catalog.
          {!rule && ' New rules start disabled — enable them from the list.'}
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              select
              label="Store"
              value={store}
              onChange={e => setStore(e.target.value)}
              disabled={rule != null || fixedStore != null}
              helperText={
                rule != null
                  ? 'A rule’s store cannot change — delete and recreate instead.'
                  : `The storefront this rule applies to (${CURRENCIES[store]}).`
              }
              sx={{ flex: 1 }}
            >
              {STORES.map(value => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Priority"
              value={priority}
              onChange={e => setPriority(Number(e.target.value))}
              helperText="When two rules want the same items, the higher priority (lower number) takes them first."
              sx={{ flex: 1 }}
            >
              {PRIORITIES.map(value => (
                <MenuItem key={value} value={value}>
                  {PRIORITY_LABELS[value]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="e.g. 2x DCOP Standard 1-pack → 2-pack kit"
            fullWidth
          />

          {catalogError && (
            <Alert
              severity="error"
              action={
                <Button size="small" onClick={loadCatalog}>
                  Retry
                </Button>
              }
            >
              Failed to load the {store} catalog — the SKU dropdowns need it.
            </Alert>
          )}
          {!catalog && !catalogError && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                Loading the {store} catalog…
              </Typography>
            </Stack>
          )}

          <Typography variant="subtitle2">Components</Typography>
          {components.map((row, index) => {
            const selected = skuOptionFor(row.sku);
            return (
              <Stack key={index} spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Autocomplete<SkuOption>
                    options={skuOptions}
                    getOptionLabel={option => option.sku}
                    isOptionEqualToValue={(option, value) => option.sku === value.sku}
                    value={selected}
                    onChange={(_, option) =>
                      setComponents(prev =>
                        prev.map((r, i) => (i === index ? { ...r, sku: option?.sku ?? '' } : r)),
                      )
                    }
                    loading={catalog == null}
                    renderOption={(props, option) => (
                      <li {...props} key={option.sku}>
                        <Stack>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {option.sku}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {productSummary(option.variants)} — {inventorySummary(option.variants)}
                          </Typography>
                        </Stack>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Component SKU"
                        size="small"
                        error={row.sku.trim() === ''}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Qty"
                    value={row.quantity}
                    onChange={e =>
                      setComponents(prev =>
                        prev.map((r, i) => (i === index ? { ...r, quantity: e.target.value } : r)),
                      )
                    }
                    error={!Number.isInteger(Number(row.quantity)) || Number(row.quantity) < 1}
                    sx={{ width: 80 }}
                    size="small"
                  />
                  <IconButton
                    size="small"
                    disabled={components.length === 1}
                    onClick={() => setComponents(prev => prev.filter((_, i) => i !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {selected && selected.variants.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                    {productSummary(selected.variants)} — {inventorySummary(selected.variants)}
                    {adminLink(selected.variants[0], 'Open in Shopify admin to verify')}
                  </Typography>
                )}
                {selected?.missing && (
                  <Typography variant="caption" color="error" sx={{ pl: 1 }}>
                    Not in today&apos;s {store} catalog — this SKU will never match.
                  </Typography>
                )}
              </Stack>
            );
          })}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setComponents(prev => [...prev, { sku: '', quantity: '1' }])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add component
          </Button>

          <Typography variant="subtitle2">Kit</Typography>
          <Autocomplete<CatalogVariant>
            options={kitOptions}
            getOptionLabel={option => `${option.productTitle} / ${option.variantTitle}`}
            isOptionEqualToValue={(option, value) => option.variantId === value.variantId}
            value={kitVariant}
            onChange={(_, option) => {
              setKitVariantId(option ? String(option.variantId) : '');
              setKitSku(option?.sku ?? '');
            }}
            loading={catalog == null}
            renderOption={(props, option) => (
              <li {...props} key={option.variantId}>
                <Stack>
                  <Typography variant="body2">
                    {option.productTitle} / {option.variantTitle}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.sku} · {CURRENCIES[store]} {option.price} ·{' '}
                    {option.shipbobStocked
                      ? `${(option.shipbobQuantity ?? 0).toLocaleString()} at ShipBob`
                      : `${option.inventoryQuantity.toLocaleString()} in stock`}{' '}
                    · {option.productStatus}
                  </Typography>
                </Stack>
              </li>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Kit variant"
                size="small"
                error={(kitVariantId !== '' && kitVariantBad) || savedKitUnfulfillable}
                helperText={
                  hiddenKitCount > 0
                    ? `Only variants ShipBob stocks${
                        shipbobLocation ? ` at ${shipbobLocation.name}` : ''
                      } can be a kit — ${hiddenKitCount} not stocked there are hidden. Its price must be at least the full component value; the job discounts it down to what the customer paid.`
                    : 'The pre-kitted variant that replaces the components. Its price must be at least the full component value — the job discounts it down to what the customer actually paid.'
                }
              />
            )}
          />
          {kitVariant && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              {kitVariant.sku} · {CURRENCIES[store]} {kitVariant.price} ·{' '}
              {kitVariant.shipbobStocked
                ? `${(kitVariant.shipbobQuantity ?? 0).toLocaleString()} at ShipBob`
                : `${kitVariant.inventoryQuantity.toLocaleString()} in stock`}{' '}
              · {kitVariant.productStatus}
              {adminLink(kitVariant, 'Open in Shopify admin to verify')}
            </Typography>
          )}
          {savedKitUnfulfillable && (
            <Alert severity="error">
              This rule&apos;s kit is not stocked at ShipBob
              {shipbobLocation ? ` (${shipbobLocation.name})` : ''}, so ShipBob could not fulfil it.
              The job skips the rule rather than stranding an order — sync the product to ShipBob,
              or pick a different kit.
            </Alert>
          )}
          {!kitVariant && catalog != null && kitVariantId !== '' && !kitVariantBad && (
            <Typography variant="caption" color="error" sx={{ pl: 1 }}>
              Saved kit variant {kitVariantId} ({kitSku || 'no SKU'}) is not in today&apos;s {store}{' '}
              catalog — verify it still exists.
            </Typography>
          )}
        </Stack>
        {ruleIdTaken && (
          <Alert severity="error" sx={{ mt: 2 }}>
            A rule with id &apos;{ruleId}&apos; already exists.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Tooltip
          title={
            canPreview
              ? 'See how this rule — including any unsaved edits — would apply to real orders'
              : 'Pick the component SKUs and quantities first'
          }
        >
          <span>
            <Button
              startIcon={<ScienceOutlinedIcon />}
              disabled={!canPreview}
              onClick={() => setPreviewOpen(true)}
            >
              Preview
            </Button>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          {saving ? 'Saving…' : 'Save rule'}
        </Button>
      </DialogActions>

      {/* Previews the DRAFT, so edits made above are reflected without saving. */}
      <PreviewDialog
        open={previewOpen}
        rule={draftRule}
        store={store}
        unsaved={rule == null || isDirty}
        onClose={() => setPreviewOpen(false)}
      />
    </Dialog>
  );
}
